import { AlertTriangle, Calendar, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface RescheduleBannerProps {
  currentStartUtc: string;
  timezone: string;
  cutoffHours: number;
  onCancel: () => void;
}

function formatDate(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  });
}

function formatTime(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
}

function getHoursUntil(utcString: string): number {
  return (new Date(utcString).getTime() - Date.now()) / 3600000;
}

export function RescheduleBanner({ currentStartUtc, timezone, cutoffHours, onCancel }: RescheduleBannerProps) {
  const { t } = useTranslation();

  const hoursUntil = getHoursUntil(currentStartUtc);
  const isPastCutoff = hoursUntil <= cutoffHours;
  const isNearCutoff = hoursUntil <= cutoffHours * 2 && !isPastCutoff;

  const date = formatDate(currentStartUtc, timezone);
  const time = formatTime(currentStartUtc, timezone);

  return (
    <div className='mb-6 space-y-2'>
      <div className='flex items-start justify-between rounded-lg border border-blue-200 bg-blue-50 p-4'>
        <div className='flex items-start gap-3'>
          <Calendar className='mt-0.5 h-4 w-4 shrink-0 text-blue-500' />
          <p className='text-sm text-blue-800'>{t('booking.reschedule.banner', { date, time })}</p>
        </div>
        <button
          type='button'
          onClick={onCancel}
          className='ml-4 shrink-0 rounded p-1 text-blue-400 hover:bg-blue-100 hover:text-blue-600'>
          <X className='h-4 w-4' />
        </button>
      </div>

      {isPastCutoff && (
        <div className='flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-red-500' />
          <p className='text-sm text-red-800'>{t('booking.reschedule.pastCutoff', { hours: cutoffHours })}</p>
        </div>
      )}

      {isNearCutoff && (
        <div className='flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-500' />
          <p className='text-sm text-amber-800'>{t('booking.reschedule.cutoffWarning', { hours: cutoffHours })}</p>
        </div>
      )}
    </div>
  );
}
